import React from "react";
import ReactDOM from "react-dom";
import "./_selectDropdown.scss";

// ── Single-select dropdown ───────────────────────────────────────────────────
// Port of the Expense module's CheckboxDropdown (custom panel, click-outside to
// close, finance styling) but selecting a single value. Shared by the timetable
// filters and the Period / TimeTable creation popups.
//
// The options panel is rendered in a portal (fixed-positioned at the trigger)
// so it is never clipped by scrollable / overflow-hidden containers such as a
// Bootstrap modal body.
//
// Props:
//   options     [{ value, label }]
//   value       currently selected value
//   placeholder shown when nothing is selected
//   onSelect    (value) => void   — emits "0" when the clear row is chosen
//   showClear   include a top "clear" row (default true)
class SelectDropdown extends React.Component {
    constructor(props) {
        super(props);
        this.state = { open: false, rect: null };
        this.triggerRef = React.createRef();
        this.panelRef = React.createRef();
    }

    componentDidMount() { document.addEventListener("mousedown", this.handleOutside); }

    componentWillUnmount() {
        document.removeEventListener("mousedown", this.handleOutside);
        window.removeEventListener("scroll", this.reposition, true);
        window.removeEventListener("resize", this.reposition);
    }

    handleOutside = (e) => {
        const inTrigger = this.triggerRef.current && this.triggerRef.current.contains(e.target);
        const inPanel = this.panelRef.current && this.panelRef.current.contains(e.target);
        if (!inTrigger && !inPanel) this.close();
    };

    open = () => {
        const rect = this.triggerRef.current.getBoundingClientRect();
        this.setState({ open: true, rect });
        window.addEventListener("scroll", this.reposition, true);
        window.addEventListener("resize", this.reposition);
    };

    close = () => {
        this.setState({ open: false });
        window.removeEventListener("scroll", this.reposition, true);
        window.removeEventListener("resize", this.reposition);
    };

    toggle = () => { this.state.open ? this.close() : this.open(); };

    reposition = () => {
        if (this.state.open && this.triggerRef.current) {
            this.setState({ rect: this.triggerRef.current.getBoundingClientRect() });
        }
    };

    pick = (val) => {
        this.props.onSelect(val);
        this.close();
    };

    render() {
        const { options = [], value, placeholder, showClear = true } = this.props;
        const { open, rect } = this.state;
        const selected = options.find((o) => String(o.value) === String(value));

        const panel = open && rect ? ReactDOM.createPortal(
            <div
                ref={this.panelRef}
                className="ltt-dd-panel"
                style={{ position: "fixed", top: rect.bottom + 4, left: rect.left, width: rect.width, right: "auto" }}
            >
                {options.length === 0 ? (
                    <div className="ltt-dd-empty">No options</div>
                ) : (
                    <>
                        {showClear && <div className="ltt-dd-clear" onClick={() => this.pick("0")}>{placeholder}</div>}
                        {options.map((o) => {
                            const isSel = String(o.value) === String(value);
                            return (
                                <div key={o.value} className={`ltt-dd-option ${isSel ? "selected" : ""}`} onClick={() => this.pick(o.value)}>
                                    <i className={`fas fa-check ltt-dd-check ${isSel ? "show" : ""}`} />
                                    <span>{o.label}</span>
                                </div>
                            );
                        })}
                    </>
                )}
            </div>,
            document.body
        ) : null;

        return (
            <div className="ltt-dd">
                <div ref={this.triggerRef} className={`ltt-dd-trigger ${selected ? "has-value" : ""}`} onClick={this.toggle}>
                    <span className="ltt-dd-text">{selected ? selected.label : placeholder}</span>
                    <i className={`fas fa-chevron-${open ? "up" : "down"} ltt-dd-caret`} />
                </div>
                {panel}
            </div>
        );
    }
}

export default SelectDropdown;
